import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { ContextMenuPopperWrapper } from '@nx-frontend/shared/ui/context-menu';
import { $theme as $contextMenuTheme } from '@nx-frontend/shared/ui/context-menu/context-menu-store';
import { themeClassesMap } from '@nx-frontend/shared/ui/context-menu/context-menu-themes';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { openModalEvent } from '@nx-frontend/shared/ui/modal';
import { organizationImage } from '@nx-frontend/shared/ui/organization-image';
import { Spinner } from '@nx-frontend/shared/ui/spinner';
import { ToastType } from '@nx-frontend/shared/ui/toast';
import { Tooltip } from '@nx-frontend/shared/ui/tooltip';
import { WorkspaceImage, getWorkspaceIcon, getWorkspaceImage } from '@nx-frontend/shared/ui/workspace-image';
import { showCopyAccessDeniedPopup } from '@nx-frontend/shared/actions';
import { clientGetOrgWorkspaces, clientMoveNote } from '@nx-frontend/shared/api/webnotes/client';
import { useHover } from '@nx-frontend/shared/hooks';
import { useRouter } from '@nx-frontend/shared/hooks/router-proxy';
import { $note } from '@nx-frontend/shared/store/note';
import { $workspacesAvatars } from '@nx-frontend/shared/store/workspace-avatars';
import { AppAccessDeniedError, Org, TranslatesFnType } from '@nx-frontend/shared/types';
import { Workspace } from '@nx-frontend/shared/types';
import { WorkspaceAvatar } from '@nx-frontend/shared/types';
import { UiTooltipPosition } from '@nx-frontend/shared/types';
import { ContextMenuItemData, ContextMenuItemType, UiContextMenuTheme } from '@nx-frontend/shared/types';

interface GetMoveCopyMenuItemParams {
  noteId: string;
  currentOrgId: string;
  organizations?: Org[];
  workspaces?: Workspace[];
  currentWorkspaceId: string;
  toast: (params: ToastType) => void;
  copy: boolean;
}

interface OrganizationMenuItemProps {
  noteId: string;
  currentOrgId: string;
  org: Org;
  workspaces?: Workspace[];
  currentWorkspaceId: string;
  toast: (params: ToastType) => void;
  copy: boolean;
  setLoadWorkspacesFor: (state: string[]) => void;
  loadWorkspacesFor: string[];
}

interface ExtendedWorkspace extends Workspace {
  avatarStoredFileUUID?: string;
}

const baseMoveToItem: ContextMenuItemData = Object.freeze({
  id: 'move-to',
  title: 'Move to',
  icon: IconsMap.MOVE_TO,
  type: ContextMenuItemType.DEFAULT,
  subMenu: [],
});

const baseCopyToItem: ContextMenuItemData = Object.freeze({
  id: 'copy-to',
  title: 'Copy to',
  icon: IconsMap.COPY,
  type: ContextMenuItemType.DEFAULT,
  subMenu: [],
});

function extendWorkspacesWithAvatars(workspaces: Workspace[], avatars: WorkspaceAvatar[]): ExtendedWorkspace[] {
  if (!avatars.length) return workspaces;
  return workspaces.map((workspace) => {
    const workspaceAvatar = avatars.find((avatar) => workspace.globalId === avatar.workspaceId);
    if (workspaceAvatar) {
      return Object.assign({}, workspace, { avatarStoredFileUUID: workspaceAvatar.storedFileUUID });
    }
    return workspace;
  });
}

interface CopyOrgWorkspacesMenuData {
  workspaces: ExtendedWorkspace[];
  fetched: boolean;
  error: boolean;
}

const DynamicWorkspacesMenuItems: FC<{
  orgId: string;
  workspaces?: Workspace[];
  currentWorkspaceId: string;
  loadWorkspaces: boolean;
  noteId: string;
  toast: GetMoveCopyMenuItemParams['toast'];
  copy: boolean;
}> = ({ orgId, workspaces = [], currentWorkspaceId, loadWorkspaces = false, noteId, toast, copy }) => {
  const [avatars, theme, currentNote, openModal] = useUnit([
    $workspacesAvatars,
    $contextMenuTheme,
    $note,
    openModalEvent,
  ]);
  const themeClasses = themeClassesMap[theme || UiContextMenuTheme.LIGHT];
  const [orgWorkspaces, setOrgWorkspaces] = useState<CopyOrgWorkspacesMenuData>({
    workspaces: [],
    fetched: false,
    error: false,
  });
  const [isRequestFired, setIsRequestFired] = useState(false); // mutex for request
  const router = useRouter();

  const t = useTranslations('sidebar');

  const getOrgWorkspaces = useCallback(async () => {
    const orgWorkspacesResponse = await clientGetOrgWorkspaces({ orgId });
    setOrgWorkspaces({
      workspaces: orgWorkspacesResponse || [],
      fetched: true,
      error: false,
    });
  }, [orgId]);

  useEffect(() => {
    if (workspaces.length) {
      setOrgWorkspaces({
        workspaces: extendWorkspacesWithAvatars(workspaces, avatars),
        fetched: true,
        error: false,
      });
    } else {
      if (loadWorkspaces && !isRequestFired) {
        getOrgWorkspaces();
        setIsRequestFired(true);
      }
    }
  }, [getOrgWorkspaces, workspaces, avatars, loadWorkspaces, isRequestFired]);

  const onWorkspaceItemClick = useCallback(
    async (workspaceId: string) => {
      let isTaskRunned = false;
      let isErrorHandled = false;
      try {
        isTaskRunned = Boolean(
          await clientMoveNote({
            workspaceFrom: currentWorkspaceId,
            noteFrom: noteId,
            workspaceId,
            remove: !copy,
            parentId: 'default',
            titleSuffix: '',
          })
        );
      } catch (err) {
        if (err instanceof AppAccessDeniedError) {
          showCopyAccessDeniedPopup(openModal);
          isErrorHandled = true;
        }
      }
      if (!isTaskRunned && !isErrorHandled) {
        toast({ title: copy ? t('sidebar-copying_error_toast') : t('sidebar-moving_error_toast') });
      }
      if (isTaskRunned) {
        toast({ title: copy ? t('sidebar-copying_page_now') : t('sidebar-moving_page_now') });

        if (currentNote.globalId === noteId && !copy) {
          // switch to parent folder if current note is moved
          if (currentNote.parentId) {
            router.push(`/space/${currentWorkspaceId}/folder/${currentNote.parentId}`);
          } else {
            router.push(`/space/${currentWorkspaceId}/all`);
          }
        }
      }
    },
    [toast, currentWorkspaceId, noteId, copy, currentNote, router, openModal, t]
  );

  if (!orgWorkspaces.fetched) {
    return (
      <div className="w-full flex items-center justify-center py-1">
        <Spinner width={24} height={24} />
      </div>
    );
  }

  return orgWorkspaces.workspaces.length ? (
    orgWorkspaces.workspaces.map((workspace) => {
      let icon = null;
      const isCurrentWorkspace = currentWorkspaceId && workspace.globalId === currentWorkspaceId;
      if (currentWorkspaceId && workspace.globalId === currentWorkspaceId) {
        icon = <Icon icon={IconsMap.LOCK} />;
      } else if (workspace.avatarStoredFileUUID) {
        const image = getWorkspaceImage(workspace);
        if (image) {
          icon = <WorkspaceImage image={image} />;
        }
      }
      if (!icon) {
        icon = getWorkspaceIcon(workspace, 'small');
      }
      return (
        <div
          className={classNames(
            'flex relative items-center py-[6px] gap-2 px-3 select-none cursor-pointer context-menu__item group/workspace-item',
            {
              'opacity-30 cursor-default': isCurrentWorkspace,
            },
            !isCurrentWorkspace && themeClasses.item
          )}
          onClick={() => onWorkspaceItemClick(workspace.globalId)}
          key={`workspace-${workspace.globalId}`}
          data-cy={`workspace-${workspace.globalId}`}
        >
          <div className="w-4 h-4 text-neutral-500">{icon}</div>
          {workspace.title.length > 25 ? workspace.title.slice(0, 25) + '...' : workspace.title}
          {!isCurrentWorkspace && (
            <div className="ml-auto">
              <Tooltip
                message={
                  copy
                    ? t('sidebar-page_will_be_copied_to_default_folder')
                    : t('sidebar-page_will_be_moved_to_default_folder')
                }
                messageClasses="whitespace-pre"
                position={UiTooltipPosition.TOP}
              >
                <Icon
                  icon={IconsMap.QUESTION_FILL}
                  className="w-4 h-4 rounded text-neutral-500 invisible group-hover/workspace-item:visible"
                />
              </Tooltip>
            </div>
          )}
        </div>
      );
    })
  ) : (
    <div className="w-full flex items-center justify-center py-1">
      <span className="text-gray-500">{t('sidebar-no_workspaces')}</span>
    </div>
  );
};

const OrganizationsMenuItems: FC<GetMoveCopyMenuItemParams> = ({
  organizations = [],
  workspaces = [],
  currentWorkspaceId,
  currentOrgId,
  noteId,
  toast,
  copy,
}) => {
  const [loadWorkspacesFor, setLoadWorkspacesFor] = useState<string[]>([]);

  return (
    <ul className="w-full">
      {organizations.map((org, index) => (
        <OrganizationMenuItemComponent
          key={index}
          org={org}
          workspaces={workspaces}
          currentWorkspaceId={currentWorkspaceId}
          currentOrgId={currentOrgId}
          noteId={noteId}
          toast={toast}
          copy={copy}
          setLoadWorkspacesFor={setLoadWorkspacesFor}
          loadWorkspacesFor={loadWorkspacesFor}
        />
      ))}
    </ul>
  );
};

const OrganizationMenuItemComponent: FC<OrganizationMenuItemProps> = ({
  org,
  workspaces = [],
  currentWorkspaceId,
  currentOrgId,
  noteId,
  toast,
  copy,
  setLoadWorkspacesFor,
  loadWorkspacesFor,
}) => {
  const [theme] = useUnit([$contextMenuTheme]);
  const themeClasses = themeClassesMap[theme || UiContextMenuTheme.LIGHT];
  const contextMenuClasses = `inline-block rounded-md border py-1 text-sm min-w-[213px] ${themeClasses.subMenu}`;

  const elementRef = useRef<HTMLLIElement | null>(null);
  const [popperReferenceEl, setPopperReferenceEl] = useState<HTMLLIElement | null>(null);
  const isHovered = useHover(elementRef);

  const onMenuItemRef = (node: HTMLLIElement) => {
    elementRef.current = node;
    setPopperReferenceEl(node);
  };

  const onOrgMouseEnter = useCallback(
    (orgId: string) => {
      if (loadWorkspacesFor.indexOf(orgId) < 0) {
        setLoadWorkspacesFor([...loadWorkspacesFor, orgId]);
      }
    },
    [loadWorkspacesFor, setLoadWorkspacesFor]
  );

  return (
    <li
      className={classNames(
        `flex relative items-center py-1.5 gap-2 px-3 select-none cursor-pointer context-menu__item`,
        themeClasses.item,
        {
          'opacity-30': org.globalId === currentOrgId && workspaces.length <= 1,
        }
      )}
      onMouseEnter={() => onOrgMouseEnter(org.globalId)}
      key={`org-${org.globalId}`}
      data-cy={`org-${org.globalId}`}
      ref={onMenuItemRef}
    >
      <div className="w-4 h-4 text-neutral-500">
        {organizationImage(org, { size: 16, addStyles: 'rounded-sm', defaultIconWrapper: false })}
      </div>
      {org.title.length > 25 ? org.title.slice(0, 25) + '...' : org.title}
      {org.globalId !== currentOrgId || workspaces.length > 1 ? (
        <>
          <span className="ml-auto text-gray-500">
            <Icon icon={IconsMap.ARROW_RIGHT} className="w-3 h-3" />
          </span>
          {isHovered && (
            <ContextMenuPopperWrapper
              targetNode={popperReferenceEl}
              addClassNames="context-menu__sub max-h-screen z-20"
              key={org.globalId}
            >
              <ul className={classNames(`ml-1 ${contextMenuClasses}`)}>
                <li className="relative select-none">
                  <DynamicWorkspacesMenuItems
                    orgId={org.globalId}
                    workspaces={org.globalId === currentOrgId ? workspaces : undefined}
                    currentWorkspaceId={currentWorkspaceId}
                    loadWorkspaces={loadWorkspacesFor.indexOf(org.globalId) >= 0}
                    noteId={noteId}
                    toast={toast}
                    copy={copy}
                  />
                </li>
              </ul>
            </ContextMenuPopperWrapper>
          )}
        </>
      ) : null}
    </li>
  );
};

export function getMoveCopyMenuItem(
  {
    noteId,
    organizations = [],
    workspaces = [],
    currentOrgId,
    currentWorkspaceId,
    toast,
    copy = false,
  }: GetMoveCopyMenuItemParams,
  t: TranslatesFnType
) {
  if (!organizations.length || organizations.length === 1) {
    return Object.assign({}, copy ? baseCopyToItem : baseMoveToItem, {
      subMenu: [
        {
          type: ContextMenuItemType.HTML,
          html: (
            <DynamicWorkspacesMenuItems
              orgId={currentOrgId}
              workspaces={workspaces}
              currentWorkspaceId={currentWorkspaceId}
              noteId={noteId}
              loadWorkspaces={true}
              toast={toast}
              copy={copy}
            />
          ),
          additionalStyles: 'hover:bg-transparent !block !p-0',
        },
      ],
    });
  } else {
    return Object.assign({}, copy ? baseCopyToItem : baseMoveToItem, {
      title: copy ? t('sidebar.sidebar-context_menus.copy_to') : t('sidebar.sidebar-context_menus.move_to'),
      subMenu: [
        {
          type: ContextMenuItemType.HTML,
          html: (
            <OrganizationsMenuItems
              {...{ organizations, workspaces, currentOrgId, currentWorkspaceId, noteId, toast, copy }}
            />
          ),
          additionalStyles: 'hover:bg-transparent !block !p-0',
        },
      ],
    });
  }
}
